<script setup>
import { computed, useSlots } from 'vue'
import { props as utilProps, propsValidator } from '@convenia/utils'

const props = defineProps({
  /**
   * Icon to be displayed before the content.
   * @type {String}
   * @default ''
   */
  prependIcon: utilProps.icon,
  /**
   * Icon to be displayed after the content.
   * @type {String}
   * @default ''
   */
  appendIcon: utilProps.icon,
  /**
   * Determines if the label should be displayed as a block element.
   * @default 'false'
   */
  block: {
    type: Boolean,
    default: false,
  },
  /**
   * Theme of the label sign.
   * @type {String}
   * @default ''
   * @validator {Function} Checks if the value is included in the predefined themes.
   */
  theme: utilProps.theme,
})

// Slots
const slots = useSlots()
const hasPrepend = !!slots.prepend
const hasAppend = !!slots.append

// Computed
const hasPrependIcon = computed(() => !!props.prependIcon)
const hasAppendIcon = computed(() => !!props.appendIcon)
const themeClass = computed(() => `-${props.theme}`)

const bindAttributes = computed(() => ({
  class: [
    {
      [themeClass.value]: propsValidator.isValidTheme(props.theme),
      '-prepend': hasPrepend || hasPrependIcon.value,
      '-append': hasAppend || hasAppendIcon.value,
      '-block': props.block,
    },
  ],
}))
</script>

<template>
  <div class="c-label-sign" v-bind="bindAttributes">
    <span class="prepend" v-if="hasPrepend || hasPrependIcon">
      <template v-if="hasPrependIcon || (hasIcon && !hasPrepend)">
        <c-icon :icon="props.prependIcon" size="18" />
      </template>

      <template v-else>
        <slot name="prepend" />
      </template>
    </span>

    <span class="content">
      <slot />
    </span>

    <span class="append" v-if="appendIcon || hasAppend">
      <template v-if="appendIcon && !hasAppend">
        <c-icon :icon="appendIcon" size="18" />
      </template>

      <template v-else>
        <slot name="append" />
      </template>
    </span>
  </div>
</template>

<style lang="scss">
@import "./_mixins.scss";

.c-label-sign {
  --icon-size: 18px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: 1fr;
  grid-template-areas: "content content content";
  gap: 15px;
  padding: 5px 5px;
  min-width: 100px;
  height: 25px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  font-family: var(--title-font-family);
  color: color-var(text, base-30);
  background-color: color-var(text, base-05);
  border-radius: 5px;

  @include generate-themes();

  &.-block {
    width: 100%;
  }

  &.-prepend:not(.-append) {
    grid-template-columns: auto 1fr auto;
    grid-template-areas: "prepend content content";

    & > .content {
      padding-right: 15px;
    }
  }

  &.-prepend,
  &.-append {
    min-width: 100px;

    & > .prepend,
    & > .append {
      display: inline-flex;
      align-items: center;
    }
  }

  &.-prepend.-append {
    grid-template-columns: auto 1fr auto;
    grid-template-areas: "prepend content append";

    & > .content {
      text-align: center;
    }
  }

  &.-append:not(.-prepend) {
    grid-template-columns: minmax(18px, auto) 1fr auto;
    grid-template-areas: "content content append";

    & > .content {
      padding-left: 15px;
    }
  }

  &:not(.-prepend):not(.-append) {
    & > .content {
      text-align: center;
    }
  }

  & > .prepend {
    grid-area: prepend;
    overflow: hidden;
  }

  & > .content {
    grid-area: content;
    display: inline-block;
    vertical-align: middle;
    line-height: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > .append {
    grid-area: append;
    overflow: hidden;
  }
}
</style>
